import React, { lazy, Suspense, useContext, useMemo } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import RouteKey from './key';
import DefaultHeaderEmpty from '../components/DefaultHeaderEmpty';
import { parseLocationSearch } from '../utils/StringUtils';

const UsedUniqueLink = lazy(() => import('../pages/financial/UsedUniqueLink'));
const UniqueLinkExpired = lazy(() => import('../pages/financial/UniqueLinkExpired'));
const InvalidUniqueLink = lazy(() => import('../pages/financial/InvalidUniqueLink'));
const Receipt = lazy(() => import('../pages/resit/Receipt'));
const Summary = lazy(() => import('../pages/resit/Summary'));
const ValidatePaymentLink = lazy(() => import('../pages/financial/ValidatePaymentLink'));

const { used, expired, invalid, summary, receipt, resit } = RouteKey['payment-link'].child;
const { paymentLink } = RouteKey.child;

interface ResitRegistrationContext {
  toSummary: string;
}

export const PaymentLinkNavigation = React.createContext<ResitRegistrationContext>({
  toSummary: '',
});

export const usePaymentLinkNavigationContext = () => useContext(PaymentLinkNavigation);

const PaymentLinkRoute = () => {
  const { path } = useRouteMatch();

  const location = useLocation();
  const { token = '' } = parseLocationSearch(location.search);

  const resitRegistrationContextValue: ResitRegistrationContext = useMemo(
    () => ({
      toSummary: `${path}/${resit}/${summary}`,
    }),
    [path]
  );

  return (
    <PaymentLinkNavigation.Provider value={resitRegistrationContextValue}>
      <DefaultHeaderEmpty />

      <Suspense fallback={null}>
        <Switch>
          <Route path={`/${paymentLink}`} exact>
            {!token ? (
              <InvalidUniqueLink />
            ) : (
              <Redirect to={`/${paymentLink}/${resit}?token=${token}`} />
            )}
          </Route>
          <Route path={`/${paymentLink}/:examRegistrationType/${used}`}>
            <UsedUniqueLink />
          </Route>
          <Route path={`/${paymentLink}/:examRegistrationType/${expired}`}>
            <UniqueLinkExpired />
          </Route>
          <Route path={`/${paymentLink}/:examRegistrationType/${invalid}`}>
            <InvalidUniqueLink />
          </Route>

          <Route
            exact
            path={[
              `/${paymentLink}/:examRegistrationType/${receipt}`,
              `/${paymentLink}/:examRegistrationType/:uuid/${receipt}`,
            ]}
          >
            <Receipt isResitPaymentLink />
          </Route>
          <Route
            exact
            path={[
              `/${paymentLink}/:examRegistrationType/${summary}`,
              `/${paymentLink}/:examRegistrationType/:uuid/${summary}`,
            ]}
          >
            <Summary />
          </Route>
          <Route
            exact
            path={[
              `/${paymentLink}/:examRegistrationType`,
              `/${paymentLink}/:examRegistrationType/:uuid`,
            ]}
          >
            <ValidatePaymentLink />
          </Route>

          <Route path="*" exact>
            {!token ? (
              <Redirect to={`/${paymentLink}/${resit}/${invalid}`} />
            ) : (
              <Redirect to={`/${paymentLink}/${resit}?token=${token}`} />
            )}
          </Route>
        </Switch>
      </Suspense>
    </PaymentLinkNavigation.Provider>
  );
};

export default PaymentLinkRoute;
